import { createContext, useContext, useEffect, ReactNode } from 'react';
import { useAuthStore } from './store';
import { authApi } from './api';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

interface AuthContextType {
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { tokens, isAuthenticated, setAuth, clearAuth, setLoading } = useAuthStore();
  const { t } = useTranslation();

  useEffect(() => {
    const initializeAuth = async () => {
      if (tokens?.accessToken && !isAuthenticated) {
        setLoading(true);
        try {
          const user = await authApi.getProfile();
          setAuth(user, tokens);
        } catch {
          clearAuth();
        } finally {
          setLoading(false);
        }
      }
    };

    initializeAuth();
  }, [tokens, isAuthenticated, setAuth, clearAuth, setLoading]);

  const login = async (email: string, password: string) => {
    setLoading(true);
    try {
      const tokens = await authApi.login({ email, password });
      const user = await authApi.getProfile(tokens.accessToken);
      setAuth(user, tokens);
      toast.success(t('auth.loginSuccess'));
    } catch (error) {
      setLoading(false);
      throw error;
    }
  };

  const logout = async () => {
    if (tokens?.refreshToken) {
      try {
        await authApi.logout(tokens.refreshToken);
      } catch (error) {
        console.error('Logout error:', error);
        toast.error(t('auth.logoutError'));
      }
    }
    clearAuth();
    toast.success(t('auth.logoutSuccess'));
  };

  return <AuthContext.Provider value={{ login, logout }}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
